/**
 * PATH: packages/ui/src/kanban/components/KanbanCardLockedFace.tsx
 * FILE_ID: SAFETY-UI-LOCK-003
 * PURPOSE: The padlock tile rendered in place of a card's normal face when
 *          the backend marked it `access_locked` (W1-V03 safety lock).
 * WHY: A locked card's content is ALREADY redacted server-side — this face
 *      makes the lock legible (obvious padlock, per the operator spec) and
 *      carries the "Request access" flow so a blocked operator has a next
 *      step instead of a dead tile.
 * FLOW:
 *   card (access_locked, access_lock_state, access_lock_id)
 *     -> padlock face + state chip
 *     -> [Request access] -> justification -> api.requestAccessUnlock
 *     -> "Requested" (verifier decides in the org queue)
 * DEPENDENCIES:
 *   - KanbanApi.requestAccessUnlock (optional — button hides when absent)
 * RELATED:
 *   - KanbanCard.tsx (renders this face and skips the normal body)
 *   - email_kanban/services/access_lock_redaction.py (server-side redaction)
 */
import { useState } from 'react'
import { Lock } from 'lucide-react'

import type { KanbanApi } from '../api'
import type { Card } from '../types'

const STATE_LABELS: Record<string, string> = {
  LOCKED: 'Locked',
  UNLOCK_REQUESTED: 'Access requested',
  UNLOCKED_GRANTED: 'Unlocked',
  DENIED: 'Access denied',
  RELOCKED: 'Re-locked',
}

export interface KanbanCardLockedFaceProps {
  card: Card
  api?: KanbanApi
  /** Toast bridge (same channel the AI band uses). */
  notify?: (kind: 'success' | 'error' | 'info', message: string) => void
}

/**
 * Self-contained: manages its own request state so the host card stays a
 * dumb shell. Request-access is a POST with side effects, so it is a
 * deliberate two-step (button -> justification -> send), never automatic.
 */
export function KanbanCardLockedFace({ card, api, notify }: KanbanCardLockedFaceProps) {
  const [composerOpen, setComposerOpen] = useState(false)
  const [justification, setJustification] = useState('')
  const [phase, setPhase] = useState<'idle' | 'sending' | 'requested'>(
    card.access_lock_state === 'UNLOCK_REQUESTED' ? 'requested' : 'idle',
  )

  const stateLabel =
    STATE_LABELS[card.access_lock_state ?? 'LOCKED'] ?? 'Locked'
  const canRequest =
    phase === 'idle' &&
    !!api?.requestAccessUnlock &&
    card.access_lock_id != null &&
    card.access_lock_state !== 'UNLOCK_REQUESTED'

  const send = async () => {
    if (!api?.requestAccessUnlock || card.access_lock_id == null) return
    setPhase('sending')
    try {
      await api.requestAccessUnlock(card.access_lock_id, justification.trim())
      setPhase('requested')
      setComposerOpen(false)
      notify?.('success', 'Access request sent to a verifier')
    } catch (e) {
      setPhase('idle')
      notify?.('error', e instanceof Error ? e.message : 'Request access failed')
    }
  }

  return (
    <div data-testid="kanban-card-locked-face" className="py-1">
      <div className="flex items-center gap-2">
        {/* The obvious lock: filled circular badge, not a subtle glyph. */}
        <span
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
        >
          <Lock className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-medium leading-tight">Locked item</div>
          <div className="text-[11px] text-neutral-500 dark:text-zinc-400">
            {phase === 'requested' ? 'Access requested — pending verifier' : stateLabel}
            {typeof card.attachment_count === 'number' && card.attachment_count > 0
              ? ` · ${card.attachment_count} attachment${card.attachment_count === 1 ? '' : 's'}`
              : ''}
          </div>
        </div>
      </div>

      {canRequest && !composerOpen && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setComposerOpen(true)
          }}
          className="mt-2 w-full rounded-md border border-amber-300/70 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20"
        >
          Request access
        </button>
      )}

      {composerOpen && phase !== 'requested' && (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Why do you need access?"
            rows={2}
            className="w-full resize-none rounded-md border border-neutral-200 bg-white px-2 py-1 text-[11px] text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
          />
          <div className="mt-1 flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => setComposerOpen(false)}
              className="rounded-md px-2 py-0.5 text-[11px] text-neutral-500 hover:text-neutral-700 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={phase === 'sending'}
              onClick={send}
              className="rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-amber-700 disabled:opacity-60"
            >
              {phase === 'sending' ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
